import React, {useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import {ajax} from 'rxjs/ajax';
import {Subscription} from 'rxjs';
import {groupBy, orderBy} from 'lodash';
import {AnonymousPageWrapper} from '../component-library/PageWrapper';
import {PublicCompletedEventsForLeague, PublicLeagueEventResults} from '../api/endpoints';
import {NewLeagueEventMatch} from '../domain/leagueevent/models';
import {EventResultsDisplay} from '../components/EventResultsDisplay';

interface LeagueEventSummary {
    id: number
    name: string
    dateString: string
}

export function PublicLeagueEvents() {
    const {leagueId, eventId} = useParams<{leagueId: string, eventId?: string}>()
    const [events, setEvents] = useState<LeagueEventSummary[]>([])
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
    const [results, setResults] = useState<NewLeagueEventMatch[]>([])
    const [loadingResults, setLoadingResults] = useState(false)
    const resultsSub = useRef<Subscription | null>(null)

    useEffect(() => {
        const sub = ajax.getJSON<LeagueEventSummary[]>(PublicCompletedEventsForLeague(leagueId))
            .subscribe({
                next: data => {
                    setEvents(data ?? [])
                    if (eventId) {
                        const id = parseInt(eventId, 10)
                        if (!isNaN(id)) selectEvent(id)
                    }
                },
                error: () => setEvents([])
            })
        return () => sub.unsubscribe()
    }, [leagueId]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        return () => {
            resultsSub.current?.unsubscribe()
        }
    }, [])

    function selectEvent(eventId: number) {
        if (selectedEventId === eventId) {
            resultsSub.current?.unsubscribe()
            setSelectedEventId(null)
            setResults([])
            return
        }
        resultsSub.current?.unsubscribe()
        setSelectedEventId(eventId)
        setResults([])
        setLoadingResults(true)
        resultsSub.current = ajax.getJSON<NewLeagueEventMatch[]>(PublicLeagueEventResults(eventId))
            .subscribe({
                next: data => {
                    setResults(data ?? [])
                    setLoadingResults(false)
                },
                error: () => {
                    setResults([])
                    setLoadingResults(false)
                }
            })
    }

    const byYear = groupBy(events.filter(e => !!e.dateString), e => new Date(e.dateString).getFullYear())
    const years = orderBy(Object.keys(byYear), y => parseInt(y), 'desc')

    return (
        <AnonymousPageWrapper>
            <div className="public-league-events">
                <h2>Event Results</h2>
                {years.length === 0 && <p>No completed events yet.</p>}
                {years.map(year => (
                    <section key={year}>
                        <h3>{year}</h3>
                        <ul>
                            {orderBy(byYear[year], e => e.dateString, 'desc').map(event => (
                                <li key={event.id}>
                                    <button
                                        className="link-button"
                                        onClick={() => selectEvent(event.id)}>
                                        {event.name} — {new Date(event.dateString).toLocaleDateString()}
                                    </button>
                                    {selectedEventId === event.id && (
                                        <div className="public-event-results">
                                            {loadingResults && <p>Loading results...</p>}
                                            {!loadingResults && results.length === 0 && <p>No results recorded.</p>}
                                            {!loadingResults && results.length > 0 && (
                                                <EventResultsDisplay matches={results}/>
                                            )}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </section>
                ))}
            </div>
        </AnonymousPageWrapper>
    )
}
